import React, { useEffect, useRef, useState } from "react";
import api from "../../services/api";

/**
 * Renderiza HTML cru de um plugin INLINE (dentro de um slot), no contexto do
 * app — sem iframe, sem clique. É o equivalente do openHtmlModal, mas embutido
 * no lugar do slot (ex: rodapé/conteúdo de uma mensagem).
 *
 * Fluxo: ao montar, faz POST no endpoint do plugin com o context; o plugin
 * devolve { html } (ou string). Injetamos via innerHTML. Se a action já trouxe
 * `html` estático, usa direto sem request.
 *
 * Props:
 *   routePath — rota isolada do plugin (/p/{hash}/{id})
 *   endpoint  — endpoint que devolve o HTML
 *   html      — HTML estático (opcional; dispensa o request)
 *   context   — dados enviados ao endpoint
 */
export default function PluginHtmlInline({ routePath, endpoint, html: staticHtml, context }) {
  const ref = useRef(null);
  const [html, setHtml] = useState(staticHtml || "");

  useEffect(() => {
    let alive = true;
    if (staticHtml != null) {
      setHtml(staticHtml);
      return undefined;
    }
    if (!routePath || !endpoint) return undefined;
    api
      .post(`${routePath}${endpoint}`, context || {})
      .then(({ data }) => {
        if (!alive) return;
        const out = typeof data === "string" ? data : data.html ?? data.result ?? "";
        setHtml(out);
      })
      .catch(() => {
        if (alive) setHtml("");
      });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routePath, endpoint, staticHtml, JSON.stringify(context || {})]);

  useEffect(() => {
    if (ref.current) ref.current.innerHTML = html || "";
  }, [html]);

  if (!html) return null;
  return <span ref={ref} style={{ display: "inline-flex", alignItems: "center" }} />;
}
